<template>
  <Teleport to="body">
    <Transition name="celebrate">
      <div v-if="current" class="celebration" @click="dismiss">
        <canvas ref="canvasEl" class="celebration__canvas" aria-hidden="true"></canvas>

        <div class="celebration__card" :style="cardStyle" role="alertdialog" @click.stop>
          <div class="celebration__glow" :style="{ background: accentGradient }"></div>

          <div class="celebration__badge" :style="{ background: accentGradient }">
            <img
              v-if="artwork"
              :src="artwork"
              class="celebration__badge-art"
              alt=""
              aria-hidden="true"
              @error="artworkFailed = true"
            />
            <component v-else :is="icon" class="celebration__badge-icon" aria-hidden="true" />
          </div>

          <p class="celebration__eyebrow">
            <template v-if="isStateUnlock"
              >State Unlocked · {{ current.template.tierDisplay }}</template
            >
            <template v-else>Achievement Unlocked{{ tierLabel ? ` · ${tierLabel}` : '' }}</template>
          </p>
          <h2 class="celebration__title">{{ current.template.name }}</h2>
          <p v-if="current.template.description" class="celebration__desc">
            {{ current.template.description }}
          </p>
          <p v-if="isStateUnlock && current.givarooId" class="celebration__desc">
            Givaroo ID: {{ current.givarooId }}
          </p>

          <div v-if="current.pointsAwarded" class="celebration__points" :style="{ color: accent }">
            +{{ current.pointsAwarded }} points
          </div>

          <button
            type="button"
            class="celebration__btn"
            :style="{ background: accentGradient }"
            @click="dismiss"
          >
            Awesome!
          </button>

          <p v-if="queueRemaining" class="celebration__more">{{ queueRemaining }} more to reveal</p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useStore } from 'vuex'
import { achievementFunctions } from '@/services/achievements'
import { resolveAchievementIcon } from './achievementIcons'

const POLL_INTERVAL_MS = 45000
const CELEBRATION_MS = 6500

export default {
  name: 'AchievementBanner',
  setup() {
    const store = useStore()
    const { api } = achievementFunctions()

    const queue = ref([])
    const current = ref(null)
    const canvasEl = ref(null)
    // Set when uploaded artwork 404s, so the badge falls back to its icon
    // rather than showing a broken image mid-celebration.
    const artworkFailed = ref(false)
    const handledIds = new Set()
    let pollTimer = null
    let rafId = null
    let onResize = null

    const isAuthenticated = computed(() => store.getters.isLoggedIn)
    const isFundraiser = computed(() => store.getters.isFundraiser)

    const icon = computed(() => resolveAchievementIcon(current.value?.template?.icon))
    const artwork = computed(() =>
      artworkFailed.value ? null : current.value?.template?.image || null,
    )
    const isStateUnlock = computed(() => current.value?.kind === 'state')
    const accent = computed(() => current.value?.template?.bannerColor || '#B57E14')
    const accentGradient = computed(
      () => `linear-gradient(135deg, ${accent.value} 0%, ${shade(accent.value, -20)} 100%)`,
    )
    const cardStyle = computed(() => ({ '--accent': accent.value }))
    const tierLabel = computed(() => {
      if (isStateUnlock.value) return ''
      const tier = current.value?.template?.tierDisplay
      return tier && tier !== 'None' ? tier : ''
    })
    const queueRemaining = computed(() => queue.value.length)

    // Darken/lighten a hex colour by a percentage.
    function shade(hex, percent) {
      const clean = (hex || '').replace('#', '')
      if (clean.length !== 6) return hex
      const num = parseInt(clean, 16)
      const amt = Math.round(2.55 * percent)
      const clamp = (v) => Math.max(0, Math.min(255, v))
      const r = clamp((num >> 16) + amt)
      const g = clamp(((num >> 8) & 0x00ff) + amt)
      const b = clamp((num & 0x0000ff) + amt)
      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`
    }

    // ---- Canvas celebration: confetti bursts (fireworks) + rising balloons ----
    const PALETTE = ['#FDD835', '#2F7D6B', '#7A5CD0', '#EF5350', '#42A5F5', '#FF7043', '#26C6DA']

    function startCelebration() {
      const canvas = canvasEl.value
      if (!canvas) return
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

      const ctx = canvas.getContext('2d')
      const dpr = window.devicePixelRatio || 1
      let w = window.innerWidth
      let h = window.innerHeight

      const setSize = () => {
        w = window.innerWidth
        h = window.innerHeight
        canvas.width = w * dpr
        canvas.height = h * dpr
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      }
      setSize()
      onResize = setSize
      window.addEventListener('resize', onResize)

      const colors = [accent.value, ...PALETTE]
      const confetti = []
      const balloons = []

      const rand = (min, max) => min + Math.random() * (max - min)
      const pick = (arr) => arr[Math.floor(Math.random() * arr.length)]

      // A radial "firework" burst of confetti at (x, y).
      const burst = (x, y, count) => {
        for (let i = 0; i < count; i++) {
          const angle = rand(0, Math.PI * 2)
          const speed = rand(3, 9)
          confetti.push({
            x,
            y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed - rand(0, 3),
            size: rand(5, 11),
            color: pick(colors),
            rot: rand(0, Math.PI * 2),
            vr: rand(-0.3, 0.3),
            life: 1,
            decay: rand(0.006, 0.012),
          })
        }
      }

      const makeBalloon = () => ({
        x: rand(0.08, 0.92) * w,
        y: h + rand(20, 220),
        vy: rand(-1.9, -1.1),
        size: rand(24, 40),
        color: pick(colors),
        phase: rand(0, Math.PI * 2),
        sway: rand(12, 30),
        swaySpeed: rand(0.01, 0.025),
      })

      // Opening: a big centre burst + two lower "cannons".
      burst(w / 2, h * 0.42, 90)
      burst(w * 0.2, h * 0.7, 40)
      burst(w * 0.8, h * 0.7, 40)
      for (let i = 0; i < 10; i++) balloons.push(makeBalloon())

      const startTs = performance.now()
      let lastFirework = 0

      const frame = (ts) => {
        const elapsed = ts - startTs
        ctx.clearRect(0, 0, w, h)

        // Extra fireworks during the first ~2.5s.
        if (elapsed < 2500 && ts - lastFirework > 450) {
          lastFirework = ts
          burst(rand(0.2, 0.8) * w, rand(0.2, 0.5) * h, 45)
        }

        // Confetti
        for (let i = confetti.length - 1; i >= 0; i--) {
          const p = confetti[i]
          p.vy += 0.12 // gravity
          p.vx *= 0.99
          p.x += p.vx
          p.y += p.vy
          p.rot += p.vr
          p.life -= p.decay
          if (p.life <= 0 || p.y > h + 40) {
            confetti.splice(i, 1)
            continue
          }
          ctx.save()
          ctx.globalAlpha = Math.max(0, Math.min(1, p.life))
          ctx.translate(p.x, p.y)
          ctx.rotate(p.rot)
          ctx.fillStyle = p.color
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6)
          ctx.restore()
        }

        // Balloons
        for (let i = balloons.length - 1; i >= 0; i--) {
          const b = balloons[i]
          b.phase += b.swaySpeed
          b.y += b.vy
          const bx = b.x + Math.sin(b.phase) * b.sway
          if (b.y < -b.size * 3) {
            balloons.splice(i, 1)
            continue
          }
          // string
          ctx.strokeStyle = 'rgba(255,255,255,0.5)'
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(bx, b.y + b.size)
          ctx.quadraticCurveTo(bx + 6, b.y + b.size * 1.8, bx, b.y + b.size * 2.4)
          ctx.stroke()
          // body
          ctx.fillStyle = b.color
          ctx.beginPath()
          ctx.ellipse(bx, b.y, b.size * 0.8, b.size, 0, 0, Math.PI * 2)
          ctx.fill()
          // highlight
          ctx.fillStyle = 'rgba(255,255,255,0.35)'
          ctx.beginPath()
          ctx.ellipse(
            bx - b.size * 0.28,
            b.y - b.size * 0.35,
            b.size * 0.18,
            b.size * 0.28,
            0,
            0,
            Math.PI * 2,
          )
          ctx.fill()
        }

        // Keep going while celebrating or particles remain.
        if (elapsed < CELEBRATION_MS || confetti.length || balloons.length) {
          rafId = requestAnimationFrame(frame)
        } else {
          ctx.clearRect(0, 0, w, h)
        }
      }
      rafId = requestAnimationFrame(frame)
    }

    function stopCelebration() {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = null
      if (onResize) {
        window.removeEventListener('resize', onResize)
        onResize = null
      }
    }

    // ---- Queue + data flow ----
    const showNext = () => {
      if (current.value || queue.value.length === 0) return
      artworkFailed.value = false
      current.value = queue.value.shift()
      // Wait a tick for the canvas element to mount, then fire the effects.
      requestAnimationFrame(() => startCelebration())
    }

    const dismiss = async () => {
      if (!current.value) return
      stopCelebration()
      const item = current.value
      current.value = null

      try {
        if (item.kind === 'state') {
          await api.csc.markStatesSeen({ ids: [item.id] })
        } else {
          await api.csc.markSeen({ ids: [item.id] })
        }
      } catch (err) {
        console.error('Failed to acknowledge celebration:', err)
      }

      setTimeout(showNext, 500)
    }

    const fetchUnseen = async () => {
      if (!isAuthenticated.value || !isFundraiser.value) return
      try {
        const [unseen, unseenStates] = await Promise.all([
          api.csc.getUnseen(),
          api.csc.getUnseenStates(),
        ])
        for (const achievement of unseen) {
          if (!handledIds.has(achievement.id)) {
            handledIds.add(achievement.id)
            queue.value.push(achievement)
          }
        }
        for (const unlock of unseenStates) {
          if (!handledIds.has(unlock.id)) {
            handledIds.add(unlock.id)
            queue.value.push(unlock)
          }
        }
        showNext()
      } catch (err) {
        console.error('Failed to fetch celebrations:', err)
      }
    }

    const handleExternalRefresh = () => fetchUnseen()

    onMounted(() => {
      if (!isFundraiser.value) return
      fetchUnseen()
      pollTimer = setInterval(fetchUnseen, POLL_INTERVAL_MS)
      window.addEventListener('new-achievement', handleExternalRefresh)
    })

    onUnmounted(() => {
      clearInterval(pollTimer)
      stopCelebration()
      window.removeEventListener('new-achievement', handleExternalRefresh)
    })

    return {
      current,
      canvasEl,
      icon,
      artwork,
      artworkFailed,
      isStateUnlock,
      accent,
      accentGradient,
      cardStyle,
      tierLabel,
      queueRemaining,
      dismiss,
    }
  },
}
</script>

<style scoped>
.celebration {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(17, 24, 39, 0.55);
  backdrop-filter: blur(2px);
}

.celebration__canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.celebration__card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 22rem;
  padding: 2rem 1.75rem 1.75rem;
  border-radius: 1.5rem;
  background: #fff;
  text-align: center;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.32), 0 4px 12px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  animation: card-pop 0.5s cubic-bezier(0.22, 1.3, 0.36, 1) both;
}

.celebration__glow {
  position: absolute;
  top: -60%;
  left: -20%;
  width: 140%;
  height: 120%;
  opacity: 0.14;
  filter: blur(30px);
  pointer-events: none;
}

.celebration__badge {
  position: relative;
  display: grid;
  place-items: center;
  width: 5rem;
  height: 5rem;
  margin: 0 auto 1rem;
  border-radius: 1.25rem;
  color: #fff;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.22);
  animation: badge-pop 0.6s 0.12s cubic-bezier(0.22, 1.4, 0.36, 1) both;
}

.celebration__badge-icon {
  width: 2.75rem;
  height: 2.75rem;
}

.celebration__badge-art {
  width: 3.5rem;
  height: 3.5rem;
  object-fit: contain;
}

.celebration__eyebrow {
  position: relative;
  margin: 0;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
}

.celebration__title {
  position: relative;
  margin: 0.375rem 0 0;
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.15;
  color: #111827;
  text-wrap: balance;
}

.celebration__desc {
  position: relative;
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  line-height: 1.4;
  color: #6b7280;
}

.celebration__points {
  position: relative;
  margin: 1rem 0 0;
  font-size: 1.125rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.celebration__btn {
  position: relative;
  margin: 1.25rem 0 0;
  padding: 0.7rem 1.75rem;
  border-radius: 999px;
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 700;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  transition: transform 0.12s ease, filter 0.12s ease;
}

.celebration__btn:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
}

.celebration__btn:active {
  transform: translateY(0);
}

.celebration__more {
  position: relative;
  margin: 0.875rem 0 0;
  font-size: 0.75rem;
  color: #9ca3af;
}

@keyframes card-pop {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes badge-pop {
  0% {
    transform: scale(0) rotate(-25deg);
  }
  60% {
    transform: scale(1.15) rotate(8deg);
  }
  100% {
    transform: scale(1) rotate(0);
  }
}

/* Overlay fade */
.celebrate-enter-active {
  transition: opacity 0.3s ease;
}
.celebrate-leave-active {
  transition: opacity 0.4s ease;
}
.celebrate-enter-from,
.celebrate-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .celebration__card,
  .celebration__badge {
    animation: none;
  }
}
</style>
